import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import { Actions as ActionsIcon, ArrowRight } from "@/components/icons";
import { Pdf, DocsFile, Document, ImageFile } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type { LibraryFolderItem, LibraryItem } from "../types/library.types";
import { LibraryShareType } from "../types/library.types";
import { libraryApi, libraryScopeFromBasePath } from "../services/library.api";
import AddFolderModal from "../components/AddFolderModal";
import AddDocumentModal from "../components/AddDocumentModal";
import DocumentViewerModal from "../components/DocumentViewerModal";
import ConfirmModal from "@/components/ui/confirm-modal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { LibraryBasePath } from "./LibraryPage";
import styles from "./LibraryPage.module.scss";

interface LibraryFolderDetailPageProps {
  basePath?: LibraryBasePath;
}
import folderDetailStyles from "./LibraryFolderDetailPage.module.scss";

const FILE_ICON_MAP: Record<
  string,
  React.ComponentType<{ width?: number; height?: number }>
> = {
  pdf: Pdf,
  doc: DocsFile,
  xls: Document,
  txt: Document,
  ppt: Document,
  png: ImageFile,
};

function getFileIcon(fileType: string) {
  const Icon = FILE_ICON_MAP[fileType.toLowerCase()] ?? Document;
  return <Icon width={20} height={20} />;
}

const FolderIconSmall: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 72 72" fill="none" aria-hidden>
    <path
      d="M31.5 13.5H8.23544C4.92977 13.5 2.25 15.8113 2.25 19.125V29.25C2.25 25.9363 4.92977 23.25 8.23544 23.25L31.5 22.5L36 18L31.5 13.5Z"
      fill="url(#folderGrad0)"
    />
    <path
      d="M2.25 28.5C2.25 25.1863 5.12817 22.5 8.67857 22.5H31.5L36 18H63.3214C66.8718 18 69.75 20.6863 69.75 24V57.0001C69.75 60.3138 66.8718 63.0001 63.3214 63.0001H8.67857C5.12817 63.0001 2.25 60.3138 2.25 57.0001L2.25 28.5Z"
      fill="url(#folderGrad1)"
    />
    <defs>
      <linearGradient
        id="folderGrad0"
        x1="19.125"
        y1="11.25"
        x2="19.125"
        y2="29.25"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E6A200" />
        <stop offset="1" stopColor="#C68600" />
      </linearGradient>
      <linearGradient
        id="folderGrad1"
        x1="36"
        y1="15.75"
        x2="36"
        y2="63"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFD766" />
        <stop offset="1" stopColor="#FEC52C" />
      </linearGradient>
    </defs>
  </svg>
);

const FOLDER_DETAIL_SORT_OPTIONS = [
  { value: "type_asc", label: i18n.t("librarySort.foldersFirst") },
  { value: "type_desc", label: i18n.t("librarySort.filesFirst") },
  { value: "name_asc", label: i18n.t("librarySort.nameAsc") },
  { value: "name_desc", label: i18n.t("librarySort.nameDesc") },
  { value: "createdAt_desc", label: i18n.t("librarySort.createdAtDesc") },
  { value: "createdAt_asc", label: i18n.t("librarySort.createdAtAsc") },
  { value: "updatedAt_desc", label: i18n.t("librarySort.updatedAtDesc") },
  { value: "updatedAt_asc", label: i18n.t("librarySort.updatedAtAsc") },
];

const LibraryFolderDetailPage: React.FC<LibraryFolderDetailPageProps> = ({
  basePath = "admin-library",
}) => {
  const prefix = `/dashboard/${basePath}`;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { folderId } = useParams<{ folderId: string }>();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.LIBRARY_CREATE);
  const canDelete = hasPermission(PERMISSIONS.LIBRARY_DELETE);
  const isClientLibrary = basePath === "library";
  const location = useLocation();
  const folderName =
    (location.state as { folderName?: string } | null)?.folderName ??
    t("library.folder.unnamed");

  const [items, setItems] = useState<LibraryItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsSort, setItemsSort] = useState("updatedAt_desc");
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [addFolderModalMode, setAddFolderModalMode] = useState<
    "create" | "add"
  >("create");
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [viewerDocument, setViewerDocument] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!folderId) return;
    setItemsLoading(true);
    try {
      const [sortBy, sortOrder] = itemsSort.split("_") as [
        "name" | "createdAt" | "type" | "updatedAt",
        "asc" | "desc",
      ];
      const res = await libraryApi.getItems({
        scope: libraryScopeFromBasePath(basePath),
        parentItemId: folderId,
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
        sortBy,
        sortOrder,
      });
      const result = res as { data: LibraryItem[]; total: number };
      setItems(result.data);
      setTotalItems(result.total);
    } finally {
      setItemsLoading(false);
    }
  }, [basePath, folderId, currentPage, pageSize, searchTerm, dateFilter, itemsSort]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearch = (value: string) => setSearchTerm(value);
  const handleSortChange = (option: { value: string }) => {
    if (option?.value) {
      setItemsSort(option.value);
      setCurrentPage(1);
    }
  };
  const handleFilterChange = (filters: Record<string, unknown>) => {
    const dateVal = filters.updatedDate;
    setDateFilter(
      dateVal instanceof Date
        ? dateVal.toISOString()
        : (dateVal as string) || null,
    );
  };

  const openAddFolderModal = (mode: "create" | "add") => {
    setAddFolderModalMode(mode);
    setAddFolderModalOpen(true);
  };
  const handleAddFolderModalClose = () => setAddFolderModalOpen(false);
  const handleAddFolderSubmit = async (payload: {
    folderName: string;
    share: "everyone" | "admins" | "custom";
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    uploadedFileIds?: string[];
    files?: File[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      const { id: newFolderId } = await libraryApi.createFolder({
        name: payload.folderName,
        parentItemId: folderId ?? undefined,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      const hasFiles =
        (payload.uploadedFileIds?.length ?? 0) > 0 ||
        (payload.files?.length ?? 0) > 0;
      if (hasFiles) {
        await libraryApi.uploadDocuments({
          fileIds: payload.uploadedFileIds,
          files: payload.files,
          parentItemId: newFolderId,
          shareType,
          sharedUserIds:
            payload.share === "custom" ? payload.sharedUserIds : undefined,
          sharedDepartmentIds:
            payload.share === "custom"
              ? payload.sharedDepartmentIds
              : undefined,
        });
        toast.success(
          t("library.folder.createdWithFiles"),
        );
      } else {
        toast.success(t("library.folder.created"));
      }
      handleAddFolderModalClose();
      fetchItems();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleAddDocumentModalClose = () => setAddDocumentModalOpen(false);
  const handleAddDocumentSubmit = async (payload: {
    share: "everyone" | "admins" | "custom";
    uploadedFileIds?: string[];
    files?: File[];
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      await libraryApi.uploadDocuments({
        fileIds: payload.uploadedFileIds?.length
          ? payload.uploadedFileIds
          : undefined,
        files: payload.uploadedFileIds?.length ? undefined : payload.files,
        parentItemId: folderId ?? undefined,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      toast.success(t("library.document.uploaded"));
      handleAddDocumentModalClose();
      fetchItems();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleFolderOpen = (folder: LibraryFolderItem) => {
    navigate(`${prefix}/folders/${folder.id}`, {
      state: { folderName: folder.name },
    });
  };

  const handleDropdownToggle = (id: string, open: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: open }));
  };

  const dateFilterField = {
    name: "updatedDate",
    label: t("library.filters.updatedDate"),
    type: "date" as const,
    placeholder: t("library.filters.updatedDatePlaceholder"),
  };

  const itemColumns: TableColumn<LibraryItem>[] = [
    {
      header: t("library.documents.cols.name"),
      accessor: (row) => (
        <div className={styles.documentNameCell}>
          <span className={styles.fileIconWrap}>
            {row.kind === "folder" ? (
              <FolderIconSmall />
            ) : (
              getFileIcon(row.fileType)
            )}
          </span>
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.uploadedBy"),
      accessor: (row) =>
        row.kind === "folder" ? (row.uploadedBy ?? "—") : row.uploadedBy,
    },
    {
      header: t("library.documents.cols.lastUpdated"),
      accessor: (row) => row.lastUpdatedFormatted,
    },
    {
      header: t("library.documents.cols.fileSize"),
      accessor: (row) => (row.kind === "file" ? row.fileSize : "—"),
    },
    {
      header: t("library.documents.cols.sharedWith"),
      accessor: (row) =>
        row.kind === "file" ? (
          <div className={styles.sharedWith}>
            <div className={styles.avatarStack}>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={styles.avatarCircle}
                  title={`User ${i}`}
                >
                  {i}
                </div>
              ))}
            </div>
            <span className={styles.plusBadge}>
              {row.sharedWithCount != null ? `+${row.sharedWithCount}` : "—"}
            </span>
          </div>
        ) : (
          "—"
        ),
    },
    {
      header: t("library.documents.cols.action"),
      accessor: (row) => {
        if (row.kind === "folder") {
          return (
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleFolderOpen(row);
              }}
            >
              {t("library.folder.open")}
            </button>
          );
        }
        const isOpen = openDropdowns[row.id] ?? false;
        return (
          <Popover
            content={
              <div className={styles.menuDropdown}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (!row.downloadUrl) {
                      toast.error(
                        t("library.download.noUrl"),
                      );
                      return;
                    }
                    setViewerDocument({ url: row.downloadUrl, name: row.name });
                  }}
                >
                  {t("common.view")}
                </button>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (!row.downloadUrl) {
                      toast.error(
                        t("library.download.noUrl"),
                      );
                      return;
                    }
                    window.open(
                      row.downloadUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  {t("common.download")}
                </button>
                {canDelete && basePath !== "library" && (
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => setDeleteTarget(row)}
                  >
                    {t("common.delete")}
                  </button>
                )}
              </div>
            }
            isOpen={isOpen}
            onClose={() => handleDropdownToggle(row.id, false)}
            position="bottom"
          >
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle(row.id, !isOpen);
              }}
            >
              <ActionsIcon width={20} height={20} />
            </button>
          </Popover>
        );
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryApi.deleteItem(deleteTarget.id);
      toast.success(t("library.deleteModal.deleted"));
      setDeleteTarget(null);
      fetchItems();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!folderId) {
    return null;
  }

  return (
    <DashboardLayout>
      <Header title={t("library.title")} />

      <div className={styles.pageContainer}>
        <div className={folderDetailStyles.breadcrumb}>
          <button
            type="button"
            className={folderDetailStyles.breadcrumbLink}
            onClick={() => navigate(prefix)}
          >
            {t("library.title")}
          </button>
          <span className={folderDetailStyles.breadcrumbSep}>&gt;</span>
          <span className={folderDetailStyles.breadcrumbCurrent}>
            {folderName}
          </span>
        </div>

        <PageHeader
          searchPlaceholder={t("library.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={handleSearch}
          showSort
          sortOptions={FOLDER_DETAIL_SORT_OPTIONS}
          selectedSortValue={itemsSort}
          onSort={handleSortChange}
          filters={[dateFilterField]}
          onFilterChange={handleFilterChange}
          addButtonLabel={
            !isClientLibrary && canCreate ? t("library.addButton") : undefined
          }
          addMenuOptions={
            !isClientLibrary && canCreate
              ? [
                  {
                    label: t("library.addMenu.createFolder"),
                    onClick: () => openAddFolderModal("create"),
                  },
                  {
                    label: t("library.addMenu.addFolder"),
                    onClick: () => openAddFolderModal("add"),
                  },
                  {
                    label: t("library.addMenu.addDocument"),
                    onClick: () => setAddDocumentModalOpen(true),
                  },
                ]
              : undefined
          }
        />

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {t("library.folder.contents")}
            </h2>
            <button
              type="button"
              className={styles.sectionLink}
              aria-label={t("library.folders.viewAll")}
              onClick={() => navigate(-1)}
            >
              <ArrowRight width={20} height={20} />
            </button>
          </div>
          <div className={styles.documentsTableWrap}>
            <Table
              columns={itemColumns}
              data={items}
              isLoading={itemsLoading}
              getRowId={(row) => row.id}
              totalRecords={totalItems}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={(p) => setCurrentPage(p)}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(1);
              }}
              onRowClick={(row) => {
                if (row.kind === "folder") handleFolderOpen(row);
              }}
              renderActions={() => null}
              hasVisibleActions={false}
              openDropdowns={openDropdowns}
              contentBasedHeight
              emptyMessage={t("library.folder.noContents")}
            />
          </div>
        </section>

        <AddFolderModal
          isOpen={addFolderModalOpen}
          onClose={handleAddFolderModalClose}
          mode={addFolderModalMode}
          onSubmit={handleAddFolderSubmit}
        />

        <AddDocumentModal
          isOpen={addDocumentModalOpen}
          onClose={handleAddDocumentModalClose}
          onSubmit={handleAddDocumentSubmit}
        />

        <DocumentViewerModal
          isOpen={!!viewerDocument}
          onClose={() => setViewerDocument(null)}
          downloadUrl={viewerDocument?.url ?? null}
          title={viewerDocument?.name}
        />

        <ConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title={t("library.deleteModal.title")}
          message={t("library.deleteModal.message")}
          confirmText={t("common.delete")}
          cancelText={t("buttons.cancel")}
          loading={deleteLoading}
          confirmVariant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default LibraryFolderDetailPage;
